import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar } from "../components/common/AppBar";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Card } from "../components/common/Card";
import { admin as adminApi, ApiError } from "../lib/apiClient";
import { getIdToken } from "../lib/firebase";
import type { SubmissionRecord } from "../lib/types";
import "./Admin.css";

const ALLOWED_ADMIN_DOMAIN = "aisingapore.org";

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : fallback;
}

export function Admin() {
  const navigate = useNavigate();

  const [emails, setEmails] = useState<string[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [transferEmail, setTransferEmail] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [transferNotice, setTransferNotice] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, subs] = await Promise.all([
        adminApi.getEmailList(),
        adminApi.listSubmissions(),
      ]);
      setEmails(list.emails);
      setSubmissions(subs.submissions);
    } catch {
      /* surfaced inline per-section as needed */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleAddEmail = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setEmailBusy(true);
    try {
      const res = await adminApi.addEmail(email);
      setEmails(res.emails);
      setNewEmail("");
    } catch (err) {
      setEmailError(errMessage(err, "Couldn't add that email."));
    } finally {
      setEmailBusy(false);
    }
  };

  const handleRemove = async (email: string) => {
    setEmailError(null);
    try {
      const res = await adminApi.removeEmail(email);
      setEmails(res.emails);
    } catch (err) {
      setEmailError(errMessage(err, "Couldn't remove that email."));
    }
  };

  const handleDeleteSubmission = async (s: SubmissionRecord) => {
    if (
      !window.confirm(
        `Delete the submission from ${s.companyName || s.userEmail}? This removes the record and its stored files.`,
      )
    )
      return;
    try {
      await adminApi.deleteSubmission(s.submissionId);
      setSubmissions((prev) =>
        prev.filter((x) => x.submissionId !== s.submissionId),
      );
    } catch (err) {
      window.alert(errMessage(err, "Couldn't delete that submission."));
    }
  };

  const handleTransfer = async (e: FormEvent) => {
    e.preventDefault();
    setTransferError(null);
    setTransferNotice(null);
    const email = transferEmail.trim().toLowerCase();
    if (!email.endsWith(`@${ALLOWED_ADMIN_DOMAIN}`)) {
      setTransferError(`Admin must be an @${ALLOWED_ADMIN_DOMAIN} email.`);
      return;
    }
    setTransferBusy(true);
    try {
      await adminApi.transferAdmin(email);
      setTransferNotice(
        `Admin transferred to ${email}. Your admin access will end after your next token refresh.`,
      );
      setTransferEmail("");
      // Force-refresh our own token so claims update.
      await getIdToken(true);
    } catch (err) {
      setTransferError(errMessage(err, "Transfer failed."));
    } finally {
      setTransferBusy(false);
    }
  };

  return (
    <div className="admin">
      <AppBar />
      <div className="admin__body">
        <div className="admin__container">
          <div>
            <h1 className="admin__heading">Admin</h1>
            <div className="admin__sub">
              Manage the LADP team/approval email list, review submissions, and
              transfer admin.
            </div>
          </div>

          <Button
            variant="secondary"
            size="sm"
            className="admin__back"
            onClick={() => navigate("/")}
          >
            ← Back to workspace
          </Button>

          {/* Email/approval list */}
          <Card className="admin__panel">
            <div className="admin__panel-title">Team / approval email list</div>
            <div className="admin__panel-sub">
              Submissions are emailed to every address here, plus a copy to the
              participant.
            </div>
            {emailError && <div className="admin__error">{emailError}</div>}

            <form className="admin__add-row" onSubmit={handleAddEmail}>
              <Input
                label="Add an email"
                type="email"
                placeholder="name@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              <Button type="submit" variant="primary" loading={emailBusy}>
                Add
              </Button>
            </form>

            {loading ? (
              <div className="admin__empty">Loading…</div>
            ) : emails.length === 0 ? (
              <div className="admin__empty">No emails on the list yet.</div>
            ) : (
              <ul className="admin__list">
                {emails.map((email) => (
                  <li key={email}>
                    <span className="admin__email">{email}</span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(email)}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Submissions */}
          <Card className="admin__panel">
            <div className="admin__panel-title">Submissions</div>
            <div className="admin__panel-sub">
              All submitted project documents.
            </div>
            {loading ? (
              <div className="admin__empty">Loading…</div>
            ) : submissions.length === 0 ? (
              <div className="admin__empty">No submissions yet.</div>
            ) : (
              <table className="admin__table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Participant</th>
                    <th>Main goal</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.submissionId}>
                      <td>{s.companyName || "—"}</td>
                      <td>{s.userEmail}</td>
                      <td>{s.mainGoal}</td>
                      <td>
                        {s.submittedAt
                          ? new Date(s.submittedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteSubmission(s)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Transfer admin */}
          <Card className="admin__panel">
            <div className="admin__panel-title">Transfer admin</div>
            <div className="admin__panel-sub">
              Grant admin to another <strong>@{ALLOWED_ADMIN_DOMAIN}</strong>{" "}
              email. This removes your own admin rights.
            </div>
            {transferError && <div className="admin__error">{transferError}</div>}
            {transferNotice && (
              <div className="admin__notice">{transferNotice}</div>
            )}
            <form className="admin__add-row" onSubmit={handleTransfer}>
              <Input
                label="New admin email"
                type="email"
                placeholder={`name@${ALLOWED_ADMIN_DOMAIN}`}
                value={transferEmail}
                onChange={(e) => setTransferEmail(e.target.value)}
              />
              <Button type="submit" variant="danger" loading={transferBusy}>
                Transfer
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
